import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import API_BASE from "../../../../config";
import { authHeaders, fmtCurrency, fmtDateShort, fmtDateTime } from "../profileUtils";
import { TabSpinner, TabError, TabEmpty } from "./FamilyTab";
import s from "./FinancesTab.module.css";
import { syLabel } from "../../../../utils/schoolYear";

const INV_STATUS = {
  PAID:    { bg: "#e8f6ed", color: "#1e6f3e" },
  PARTIAL: { bg: "#fdf3d4", color: "#8a6d28" },
  UNPAID:  { bg: "#fde8e4", color: "#c0392b" },
};
const PAY_METHOD_ICON = {
  CASH: "💵", BANK: "🏦", GCASH: "📱", CARD: "💳", OTHER: "💳",
};

export default function FinancesTab({ studentId, schoolYears = [], semesters = [], studentName = "", studentNumber = "" }) {
  const navigate = useNavigate();
  const searchTerm = encodeURIComponent(studentName || studentNumber || "");
  const [invoices,          setInvoices]          = useState(null);
  const [discounts,         setDiscounts]         = useState([]);
  const [overrides,         setOverrides]         = useState([]);
  const [addlCharges,       setAddlCharges]       = useState([]);
  const [loading,           setLoading]           = useState(true);
  const [error,             setError]             = useState("");
  const [expandedDiscounts, setExpandedDiscounts] = useState(new Set());
  const [expandedPayments,  setExpandedPayments]  = useState(new Set());
  /* id → { firstName, lastName, studentNumber } for REFERRAL/SIBLING linked students */
  const [linkedStudents,    setLinkedStudents]    = useState({});

  function toggleDiscount(id) {
    setExpandedDiscounts(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  }
  function togglePayment(id) {
    setExpandedPayments(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  }

  /* Phase 1: fetch invoices, discounts, overrides, additional charges */
  useEffect(() => {
    if (!studentId) return;
    let cancelled = false;
    setLoading(true); setError("");

    Promise.all([
      fetch(`${API_BASE}/api/tuition-invoices?studentId=${studentId}`, { headers: authHeaders() })
        .then(r => r.ok ? r.json() : []),
      fetch(`${API_BASE}/api/student-discounts?studentId=${studentId}`, { headers: authHeaders() })
        .then(r => r.ok ? r.json() : []),
      fetch(`${API_BASE}/api/student-fee-overrides?studentId=${studentId}`, { headers: authHeaders() })
        .then(r => r.ok ? r.json() : []),
      fetch(`${API_BASE}/api/additional-charges?studentId=${studentId}`, { headers: authHeaders() })
        .then(r => r.ok ? r.json() : []),
    ])
      .then(([invData, discData, ovData, addlData]) => {
        if (cancelled) return;
        const invArr  = Array.isArray(invData)  ? invData  : [];
        const discArr = Array.isArray(discData) ? discData : [];
        const ovArr   = Array.isArray(ovData)   ? ovData   : [];
        const addlArr = Array.isArray(addlData) ? addlData : [];

        setDiscounts(discArr);
        setOverrides(ovArr);
        setAddlCharges(addlArr);

        /* Resolve linked students (referral / sibling) — fetch in parallel */
        const linkedIds = [...new Set(
          discArr.map(d => d.linkedStudentId).filter(Boolean)
        )];
        if (linkedIds.length > 0) {
          Promise.all(
            linkedIds.map(id =>
              fetch(`${API_BASE}/api/students/${id}`, { headers: authHeaders() })
                .then(r => r.ok ? r.json() : null)
                .catch(() => null)
            )
          ).then(results => {
            if (cancelled) return;
            const map = {};
            results.forEach(st => { if (st?.id) map[st.id] = st; });
            setLinkedStudents(map);
          });
        }

        /* Phase 2: fetch payments + history for each invoice in parallel */
        if (invArr.length === 0) {
          setInvoices([]);
          return;
        }
        return Promise.all(
          invArr.map(inv =>
            Promise.all([
              fetch(`${API_BASE}/api/tuition-payments?invoiceId=${inv.id}`, { headers: authHeaders() })
                .then(r => r.ok ? r.json() : []),
              fetch(`${API_BASE}/api/tuition-invoices/${inv.id}/history`, { headers: authHeaders() })
                .then(r => r.ok ? r.json() : []),
            ]).then(([pmts, hist]) => ({ ...inv, payments: pmts || [], history: hist || [] }))
          )
        ).then(enriched => {
          if (!cancelled) setInvoices(enriched);
        });
      })
      .catch(() => { if (!cancelled) setError("Could not load financial records."); })
      .finally(() => { if (!cancelled) setLoading(false); });

    return () => { cancelled = true; };
  }, [studentId]);

  const syById  = useMemo(() => Object.fromEntries(schoolYears.map(y => [y.id, y])), [schoolYears]);
  const semById = useMemo(() => Object.fromEntries(semesters.map(m => [m.id, m])),   [semesters]);

  /* Group invoices by school year */
  const grouped = useMemo(() => {
    if (!invoices) return [];
    const map = new Map();
    [...invoices]
      .sort((a, b) => (b.id || 0) - (a.id || 0))
      .forEach(inv => {
        const key = inv.schoolYearId ?? "unknown";
        if (!map.has(key)) map.set(key, []);
        map.get(key).push(inv);
      });
    return [...map.entries()];
  }, [invoices]);

  /* Lifetime totals */
  const totals = useMemo(() => {
    if (!invoices?.length) return null;
    let totalInvoiced = 0, totalPaid = 0;
    invoices.forEach(inv => {
      totalInvoiced += Number(inv.amount) || 0;
      inv.payments?.forEach(p => { totalPaid += Number(p.amount) || 0; });
    });
    addlCharges.forEach(c => {
      totalInvoiced += Number(c.amount) || 0;
      totalPaid     += Number(c.paidAmount) || 0;
    });
    return { totalInvoiced, totalPaid, balance: totalInvoiced - totalPaid };
  }, [invoices, addlCharges]);

  if (loading) return <TabSpinner label="Loading financial records…" />;
  if (error)   return <TabError message={error} />;
  if (!invoices?.length && !addlCharges.length) return (
    <TabEmpty
      icon="💰"
      title="No financial records"
      sub="Tuition invoices and payments will appear here once created."
    />
  );

  return (
    <div className={s.root}>
      {/* Quick-action links */}
      <div className={s.quickBar}>
        <button
          type="button"
          className={s.quickLink}
          onClick={() => navigate({ pathname: "/tuition/soa", search: `?studentId=${studentId}` })}
          title="Open SOA with this student pre-selected">
          View SOA →
        </button>
        <button
          type="button"
          className={s.quickLink}
          onClick={() => navigate({ pathname: "/admin/tuition", search: `?tab=tuition-payments&search=${searchTerm}&autoStudentId=${studentId}` })}
          title="Go to Payments tab and open payment window for this student">
          Manage Payments →
        </button>
        <button
          type="button"
          className={s.quickLink}
          onClick={() => navigate({ pathname: "/admin/tuition", search: `?tab=student-discounts&search=${searchTerm}&autoStudentId=${studentId}` })}
          title="Go to Discounts tab and open discount manager for this student">
          Manage Discounts →
        </button>
      </div>

      {/* Lifetime summary */}
      {totals && (
        <div className={s.totalBar}>
          <div className={s.totalItem}>
            <span className={s.totalLabel}>Total Invoiced</span>
            <span className={s.totalAmount}>{fmtCurrency(totals.totalInvoiced)}</span>
          </div>
          <div className={s.totalDivider} />
          <div className={s.totalItem}>
            <span className={s.totalLabel}>Total Paid</span>
            <span className={`${s.totalAmount} ${s.totalPaid}`}>{fmtCurrency(totals.totalPaid)}</span>
          </div>
          <div className={s.totalDivider} />
          <div className={s.totalItem}>
            <span className={s.totalLabel}>Outstanding Balance</span>
            <span className={`${s.totalAmount} ${totals.balance > 0 ? s.totalDue : s.totalClear}`}>
              {fmtCurrency(totals.balance)}
            </span>
          </div>
        </div>
      )}

      {/* Invoices grouped by school year */}
      {grouped.map(([syId, invList], gIdx) => {
        const sy = syById[syId];
        /* Discounts that apply to this school year */
        const syDiscounts = discounts.filter(d => String(d.schoolYearId) === String(syId));
        /* Fee override for this SY */
        const syOverride  = overrides.find(o => String(o.schoolYearId) === String(syId));
        /* Additional charges for this SY */
        const syCharges   = addlCharges.filter(c => String(c.schoolYearId) === String(syId));

        return (
          <div key={syId} className={s.yearBlock} style={{ animationDelay: `${gIdx * 60}ms` }}>
            <div className={s.yearHead}>
              <span className={s.yearDot} aria-hidden="true" />
              <span className={s.yearLabel}>{sy ? syLabel(sy.label) : "Unknown Year"}</span>
            </div>

            <div className={s.yearBody}>
              {/* Discounts section */}
              {syDiscounts.length > 0 && (
                <div className={s.discountsSection}>
                  <div className={s.miniLabel}>Discounts Applied</div>
                  {syDiscounts.map(d => {
                    const open = expandedDiscounts.has(d.id);
                    const scope = [
                      d.appliesTuition && "Tuition",
                      d.appliesMisc    && "Miscellaneous",
                      d.appliesBooks   && "Books",
                    ].filter(Boolean);
                    return (
                      <div key={d.id} className={s.discountEntry}>
                        <div
                          className={s.discountRow}
                          onClick={() => toggleDiscount(d.id)}
                          onKeyDown={e => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); toggleDiscount(d.id); }}}
                          role="button"
                          tabIndex={0}
                          aria-expanded={open}>
                          <span className={s.discountName}>
                            {d.type === "OTHERS" ? (d.customName || d.type) : d.type}
                          </span>
                          <span className={s.discountPct}>{d.percentage}% off</span>
                          <span className={s.discountScope}>
                            {scope.join(" · ") || "—"}
                          </span>
                          <span className={`${s.rowChevron} ${open ? s.chevronOpen : ""}`} aria-hidden="true">›</span>
                        </div>
                        {open && (
                          <div className={s.expandDetail}>
                            <div className={s.detailGrid}>
                              <span className={s.detailKey}>Type</span>
                              <span className={s.detailVal}>{d.type}</span>
                              {d.type === "OTHERS" && d.customName && (
                                <>
                                  <span className={s.detailKey}>Name</span>
                                  <span className={s.detailVal}>{d.customName}</span>
                                </>
                              )}
                              <span className={s.detailKey}>Percentage</span>
                              <span className={s.detailVal}>{d.percentage}%</span>
                              <span className={s.detailKey}>Applies To</span>
                              <span className={s.detailVal}>{scope.join(", ") || "—"}</span>
                              {d.linkedStudentId && (() => {
                                const linked = linkedStudents[d.linkedStudentId];
                                const label = d.type === "REFERRAL" ? "Referred Student"
                                            : d.type === "SIBLING"  ? "Sibling"
                                            : "Linked Student";
                                const name = linked
                                  ? [linked.firstName, linked.middleName, linked.lastName].filter(Boolean).join(" ")
                                  : null;
                                return (
                                  <>
                                    <span className={s.detailKey}>{label}</span>
                                    <span className={s.detailVal}>
                                      {linked ? (
                                        <span>
                                          {name || "(Unnamed)"}
                                          {linked.studentNumber && (
                                            <span className={s.detailMuted}> · #{linked.studentNumber}</span>
                                          )}
                                        </span>
                                      ) : (
                                        <span className={s.detailMuted}>Loading… (ID {d.linkedStudentId})</span>
                                      )}
                                    </span>
                                  </>
                                );
                              })()}
                              {d.notes && (
                                <>
                                  <span className={s.detailKey}>Notes</span>
                                  <span className={s.detailVal}>{d.notes}</span>
                                </>
                              )}
                              {d.createdAt && (
                                <>
                                  <span className={s.detailKey}>Date Added</span>
                                  <span className={s.detailVal}>{fmtDateTime(d.createdAt)}</span>
                                </>
                              )}
                              {d.addedBy && (
                                <>
                                  <span className={s.detailKey}>Added By</span>
                                  <span className={s.detailVal}>{d.addedBy}</span>
                                </>
                              )}
                              <span className={s.detailKey}>Ref #</span>
                              <span className={s.detailVal}>{d.id}</span>
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}

              {/* Fee override */}
              {syOverride && (
                <div className={s.overrideSection}>
                  <div className={s.miniLabel}>Fee Override</div>
                  <div className={s.overrideRow}>
                    <span>Enrollment Fee Override:</span>
                    <span className={s.overrideAmt}>{fmtCurrency(syOverride.enrollmentFee)}</span>
                    {syOverride.notes && <span className={s.overrideNote}>{syOverride.notes}</span>}
                  </div>
                </div>
              )}

              {/* Invoice cards */}
              {invList.map(inv => {
                const sem       = inv.semesterId ? semById[inv.semesterId] : null;
                const badge     = INV_STATUS[inv.status] || INV_STATUS.UNPAID;
                const totalPaid = inv.payments?.reduce((sum, p) => sum + (Number(p.amount) || 0), 0) || 0;
                const balance   = (Number(inv.amount) || 0) - totalPaid;
                return (
                  <div key={inv.id} className={s.invoiceCard}>
                    <div className={s.invoiceHead}>
                      <div>
                        <div className={s.invTitle}>
                          {sem?.label || `Invoice #${inv.id}`}
                        </div>
                        {inv.dueDate && (
                          <div className={s.invDue}>Due {fmtDateShort(inv.dueDate)}</div>
                        )}
                      </div>
                      <div className={s.invAmounts}>
                        <span className={s.invTotal}>{fmtCurrency(inv.amount)}</span>
                        <span
                          className={s.invBadge}
                          style={{ background: badge.bg, color: badge.color }}>
                          {inv.status}
                        </span>
                      </div>
                    </div>

                    {/* Payments */}
                    {inv.payments?.length > 0 && (
                      <div className={s.paymentsSection}>
                        <div className={s.miniLabel}>Payments</div>
                        <div className={s.paymentsTable}>
                          {[...inv.payments]
                            .sort((a, b) => new Date(a.paidAt) - new Date(b.paidAt))
                            .map(p => {
                              const pOpen = expandedPayments.has(p.id);
                              return (
                                <div key={p.id} className={s.payEntry}>
                                  <div
                                    className={s.payRow}
                                    onClick={() => togglePayment(p.id)}
                                    onKeyDown={e => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); togglePayment(p.id); }}}
                                    role="button"
                                    tabIndex={0}
                                    aria-expanded={pOpen}>
                                    <span className={s.payDate}>{fmtDateShort(p.paymentReceivedOn || p.paidAt)}</span>
                                    <span className={s.payMethod}>
                                      <span aria-hidden="true">{PAY_METHOD_ICON[p.method] || "💳"}</span>
                                      {" "}{p.method}
                                    </span>
                                    <span className={s.payOR}>{p.orNumber || "—"}</span>
                                    {p.referenceNo && (
                                      <span className={s.payRef}>{p.referenceNo}</span>
                                    )}
                                    <span className={s.payAmt}>{fmtCurrency(p.amount)}</span>
                                    <span className={`${s.rowChevron} ${pOpen ? s.chevronOpen : ""}`} aria-hidden="true">›</span>
                                  </div>
                                  {pOpen && (
                                    <div className={s.expandDetail}>
                                      <div className={s.detailGrid}>
                                        <span className={s.detailKey}>Date Received</span>
                                        <span className={s.detailVal}>{fmtDateTime(p.paymentReceivedOn || p.paidAt) || "—"}</span>
                                        <span className={s.detailKey}>Method</span>
                                        <span className={s.detailVal}>{PAY_METHOD_ICON[p.method] || "💳"} {p.method || "—"}</span>
                                        <span className={s.detailKey}>OR Number</span>
                                        <span className={s.detailVal}>{p.orNumber || "—"}</span>
                                        {p.referenceNo && (
                                          <>
                                            <span className={s.detailKey}>Reference No.</span>
                                            <span className={s.detailVal}>{p.referenceNo}</span>
                                          </>
                                        )}
                                        <span className={s.detailKey}>Amount</span>
                                        <span className={s.detailVal}>{fmtCurrency(p.amount)}</span>
                                        {p.notes && (
                                          <>
                                            <span className={s.detailKey}>Notes</span>
                                            <span className={s.detailVal}>{p.notes}</span>
                                          </>
                                        )}
                                        {p.createdBy && (
                                          <>
                                            <span className={s.detailKey}>Recorded By</span>
                                            <span className={s.detailVal}>{p.createdBy}</span>
                                          </>
                                        )}
                                        {p.paidAt && (
                                          <>
                                            <span className={s.detailKey}>Recorded At</span>
                                            <span className={s.detailVal}>{fmtDateTime(p.paidAt)}</span>
                                          </>
                                        )}
                                        <span className={s.detailKey}>Payment Ref #</span>
                                        <span className={s.detailVal}>{p.id}</span>
                                      </div>
                                    </div>
                                  )}
                                </div>
                              );
                            })}
                        </div>
                        <div className={s.payBalance}>
                          <span>Balance:</span>
                          <span className={balance > 0 ? s.balanceDue : s.balanceClear}>
                            {fmtCurrency(balance)}
                          </span>
                        </div>
                      </div>
                    )}

                    {/* Adjustments */}
                    {inv.history?.length > 0 && (
                      <div className={s.adjustSection}>
                        <div className={s.miniLabel}>Adjustments</div>
                        {inv.history.map(adj => (
                          <div key={adj.id} className={s.adjRow}>
                            <span className={s.adjDate}>{fmtDateShort(adj.createdAt)}</span>
                            <span className={s.adjArrow}>
                              {fmtCurrency(adj.oldAmount)} → {fmtCurrency(adj.newAmount)}
                            </span>
                            {adj.reason && <span className={s.adjReason}>{adj.reason}</span>}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}

              {/* Additional charges */}
              {syCharges.length > 0 && (
                <div className={s.chargesSection}>
                  <div className={s.chargesLabel}>Additional Charges</div>
                  {syCharges.map(c => {
                    const derived = Number(c.paidAmount || 0) >= Number(c.amount || 0) - 0.005
                      ? "PAID"
                      : Number(c.paidAmount || 0) > 0.005
                        ? "PARTIAL"
                        : "UNPAID";
                    const badge = INV_STATUS[derived] || INV_STATUS.UNPAID;
                    return (
                      <div key={c.id} className={s.chargeRow}>
                        <span className={s.chargeLabel}>{c.label}</span>
                        <span className={s.chargeAmt}>{fmtCurrency(c.amount)}</span>
                        <span
                          className={s.chargeBadge}
                          style={{ background: badge.bg, color: badge.color }}>
                          {derived}
                        </span>
                        {Number(c.paidAmount) > 0 && (
                          <span className={s.chargePaid}>Paid {fmtCurrency(c.paidAmount)}</span>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        );
      })}

      {/* Additional charges with no school year (shouldn't happen but guard) */}
      {addlCharges.filter(c => !invoices?.some(i => String(i.schoolYearId) === String(c.schoolYearId))).length > 0 && (
        <div className={s.yearBlock}>
          <div className={s.yearHead}>
            <span className={s.yearDot} aria-hidden="true" />
            <span className={s.yearLabel}>Other Charges</span>
          </div>
          <div className={s.yearBody}>
            {addlCharges
              .filter(c => !invoices?.some(i => String(i.schoolYearId) === String(c.schoolYearId)))
              .map(c => (
                <div key={c.id} className={s.chargeRow}>
                  <span className={s.chargeLabel}>{c.label}</span>
                  <span className={s.chargeAmt}>{fmtCurrency(c.amount)}</span>
                </div>
              ))}
          </div>
        </div>
      )}
    </div>
  );
}
